import React, { useEffect, useRef, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Label, Groupper, Input, Pagination, Icon, Button, Modal, Field, Toolbar, Toast, Message, Checkbox, Items } from 'react-frontier';
import { Link } from 'react-router-dom';
import { Expense, Budget, PaymentMethod, UserBankAccount, SetLoading, AssetType } from '../SiteClasses';
import { addCommas, bindClick, getDepartmentName, getAssetTypeIcon } from '../SiteUtil';
import { Dropdown } from 'semantic-ui-react';
import { ExpenseModal } from '../components';
import UploadBox, { UploadBoxRef } from '../components/UploadBox';
import UserAccess from '../UserAccess';
import classNames from 'classnames';
import moment from 'moment';
import API from '../API';

const PAGE_SIZE = 20;

interface UserExpensesProps{
	
}

enum FilterType{
	EXPENSES = 1,
	BUDGETS = 2,
}

var UserExpenses = (props: UserExpensesProps)=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [expenses, setExpenses] = useState<Expense[]>(null);
	var [budgets, setBudgets] = useState<Budget[]>(null);
	var [input, setInput] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [filter, setFilter] = useState<FilterType>(FilterType.EXPENSES);
	var [showExpenseModal, setShowExpenseModal] = useState<boolean>(false);
	var [selectedBudget, setSelectedBudget] = useState<Budget>(null);
	var [showInfoModal, setShowInfoModal] = useState<boolean>(false);
	var [showGenerateExpenseModal, setShowGenerateExpenseModal] = useState<boolean>(false);
	var [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	var [selectedMethodId, setSelectedMethodId] = useState<number>(null);
	var [selectedFile, setSelectedFile] = useState<File>(null);
	var [fileTypeId, setFileTypeId] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [accounts, setAccounts] = useState<UserBankAccount[]>(null);
	var [showAccountsModal, setShowAccountsModal] = useState<boolean>(false);
	var uploadBoxRef = useRef<UploadBoxRef>(null);

	useEffect(()=>{
		setTitle('Mis Gastos');
	}, []);

	useEffect(()=>{
		setPage(0);
		setInput('');
		if(filter !== null){
			get(0, '');
		}
	}, [filter]);

	var get = (get_page?: number, query: string = '')=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		
		setLoading(true);

		if(filter === FilterType.EXPENSES){
			API.getUserExpenses(query, offset, PAGE_SIZE).then(res=>{
				if(res.error) return setLoadError(res.message);
				return setExpenses(res.data);
			}).catch(err => {
				return setLoadError('Hubo un error al obtener tus gastos. (UGES-1)');
			}).finally(()=>{
				setLoading(false);
			})
		}else{
			API.getUserBudgets(query, offset, PAGE_SIZE).then(res=>{
				if(res.error) return setLoadError(res.message);
				return setBudgets(res.data);
			}).catch(err => {
				return setLoadError('Hubo un error al obtener tus presupuestos. (UGES-2)');
			}).finally(()=>{
				setLoading(false);
			})
		}

	};

	var onPageChange = (page: number)=>{
		setLoading(true);
		setPage(page);
		get(page, input);
	}

	var onSearchChange = (val: string)=>{
		setInput(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		setSearchTimeout(setTimeout(()=>{
			setPage(0);
			get(0, val);
		}, 500));
	}

	var onShowInfo = (budget: Budget)=>{
		setSelectedBudget(budget);
		setModalPrompts(null);
		setShowInfoModal(true);
	};

	var onShowGenerateExpense = (budget: Budget)=>{
		setSelectedBudget(budget);
		setSelectedFile(null);
		setFileTypeId(null);
		setSelectedMethodId(null);
		setModalPrompts(null);
		if(uploadBoxRef.current){
			uploadBoxRef.current.clear();
		}
		if(paymentMethods.length === 0){
			API.getPaymentMethods().then(res=>{
				if(!res.error) setPaymentMethods(res.data);
			});
		}
		setShowInfoModal(false);
		setShowGenerateExpenseModal(true);
	};

	var onShowAccountsModal = async ()=>{
		API.getUserBankAccounts().then(res=>{
			if(res.error) return Toast.error(res.message);
			setAccounts(res.data);
			setShowAccountsModal(true);
		}).catch(err=>{
			return Toast.error('Hubo un error al obtener las cuentas (GET-1)');
		});
	};

	var onAccountSelected = (a: UserBankAccount)=>{
		var b = {...selectedBudget};
		b.account = a;
		b.account_id = a.account_id;
		setSelectedBudget(b);
		setShowAccountsModal(false);
		setSelectedFile(null);
		setFileTypeId(null);
		if(uploadBoxRef.current){
			uploadBoxRef.current.clear();
		}
		setShowGenerateExpenseModal(true);
	};

	var onFileSelected = (file: File)=>{
		var type : AssetType = null;
		if(!file){
			setSelectedFile(null);
			setFileTypeId(null);
			return;
		}
		if(file.type==='application/pdf'){
			type = AssetType.PDF;
		}else if(file.type.startsWith('image/')){
			type = AssetType.IMAGE;
		}
		if(!type) return Toast.error('El archivo no es un formato válido');
		setSelectedFile(file);
		setFileTypeId(type);
	};

	var budgetToExpense = (setLoading: SetLoading)=>{
		if(!selectedMethodId){
			return setModalPrompts(['Selecciona un método de pago']);
		}
		setLoading(true);
		API.convertToExpense(selectedBudget.budget_id, selectedBudget.account_id, selectedMethodId, selectedFile, fileTypeId, selectedBudget.budget_title, selectedBudget.description).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha creado el gasto');

			setBudgets(prev => prev.map(b => 
				b.budget_id === selectedBudget.budget_id ? { ...b, expense_id: res.data.expense_id } : b
			));
			setShowGenerateExpenseModal(false);
			setSelectedBudget(null);
			setSelectedFile(null);
			setFileTypeId(null);
			if(uploadBoxRef.current){
				uploadBoxRef.current.clear();
			}
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado creando el gasto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};
	
	if(loadError) { return <Header text='Error' subtext={loadError} />};
	if(!expenses && !budgets) { return <Header text='Cargando datos' loading/> };

	var currentData = filter === FilterType.EXPENSES ? expenses : budgets;

	return <div>
		<Groupper
			title={filter === FilterType.EXPENSES ? 'Mis Gastos' : 'Mis Presupuestos'}
			style={{ marginTop: 20, paddingBottom: 0 }}
			width={1100}
			titleRight={filter === FilterType.EXPENSES &&
				<Button 
					text='Agregar' 
					iconName='circle-plus' 
					color='black'
					onClick={() => setShowExpenseModal(true)}
				/>
			}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
				<Input 
					placeholder='Buscar...' 
					value={input} 
					onChange={onSearchChange} 
					style={{ flex: 1 }} 
				/>
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button 
						text='Filtrar' 
						size='small'
						iconName='filter'
					/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item onClick={() => setFilter(filter === FilterType.EXPENSES ? null : FilterType.EXPENSES)}>
							<i className={classNames('icon circle', {
								'check': filter === FilterType.EXPENSES,
								'outline': filter !== FilterType.EXPENSES,
							})}></i>
							Gastos
						</Dropdown.Item>
						<Dropdown.Item onClick={() => setFilter(filter === FilterType.BUDGETS ? null : FilterType.BUDGETS)}>
							<i className={classNames('icon circle', {
								'check': filter === FilterType.BUDGETS,
								'outline': filter !== FilterType.BUDGETS,
							})}></i>
							Presupuestos
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			</div>
			<Table 
				striped 
				fitted 
				selectable={filter === FilterType.EXPENSES}
				style={{ marginTop: 15 }}
				headers={filter === FilterType.EXPENSES 
					? ['ID', 'Título', 'Tipo', 'Evento','Departamento', 'Creado', 'Monto', 'Estatus']
					: ['ID', 'Título', 'Tipo', 'Evento','Departamento', 'Creado', 'Monto', 'Estatus', <Icon name='wrench' />]
				}
				centeredIndexes={[6]}
				footer={(currentData && currentData.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={9} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={currentData.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)}
			>
				{loading ? (
					<Table.Cell row className='empty' colSpan={9}>
						<Header loading text='Cargando datos' />
					</Table.Cell>
				) : filter === FilterType.EXPENSES ? (
					expenses && expenses.length !== 0 ? (
						expenses.map(e => (
							<Table.Row
								compact
								key={e.expense_id}
								as={Link}
								target='_blank'
								to={e.date_id ?  `/events/${e.date_id}/expenses` : `/expenses/${e.expense_id}`}
								collapsingIndexes={[0,1, 2, 4, 5, 6]}
								centeredIndexes={[4, 7]}
								data={[
									e.expense_id,
									e.expense_title,
									e.type_name,
									e.event_name ? e.event_name : <Icon name='minus'/>,
									e.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(e.department_id)}</Label> : <Icon name='minus'/>,
									moment.unix(e.date_created).format('DD/MM/YY HH:mm'),
									'$' + addCommas(e.amount),
									<Label 
										style={{ width: 100, padding: 0 }} 
										color={e.paid ? 'green' : e.approved ? 'blue' : 'yellow'}
									>
										{e.paid ? 'Pagado' : e.approved ? 'Aprobado' : 'Pendiente'}
									</Label>,
								]}
							/>
						))
					) : (
						<Table.Cell row className='empty' colSpan={9}>
							{input ? 'No se encontraron registros' : 'No tienes gastos registrados'}
						</Table.Cell>
					)
				) : (
					budgets && budgets.length !== 0 ? (
						budgets.map(b => (
							<Table.Row
								key={b.budget_id}
								collapsingIndexes={[0,1, 2, 4, 5, 6, 8]}
								centeredIndexes={[4, 7]}
								data={[
									b.budget_id,
									b.budget_title,
									b.type_name,
									b.event_name ? b.event_name : <Icon name='minus'/>,
									b.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(b.department_id)}</Label> : <Icon name='minus'/>,
									moment.unix(b.date_created).format('DD/MM/YY HH:mm'),
									'$' + addCommas(b.amount),
									<Label 
										style={{ width: 100, padding: 0 }} 
										color={b.deleted ? 'red' : b.rejected ? 'red' : b.paid ? 'green' : b.approved ? 'blue' : 'yellow'}
									>
										{b.deleted ? 'Eliminado' : b.rejected ? 'Rechazado' : b.paid ? 'Pagado' : b.approved ? 'Aprobado' : 'Pendiente'}
									</Label>,
									<Dropdown direction='left' icon={(
										<div className="fr label" style={{ padding: '3px 10px' }}>
											<Icon name='ellipsis' style={{ margin: 0 }} />
										</div>
									)} className='icon'>
										<Dropdown.Menu direction='left'>
											<Dropdown.Item onClick={() => onShowInfo(b)}>
												<Icon name='eye' style={{ marginRight: 3 }} /> Info
											</Dropdown.Item>
										</Dropdown.Menu>
									</Dropdown>,
								]}
							/>
						))
					) : (
						<Table.Cell row className='empty' colSpan={9}>
							{input ? 'No se encontraron registros' : 'No tienes presupuestos registrados'}
						</Table.Cell>
					)
				)}
			</Table>
		</Groupper>
		<ExpenseModal 
			open={showExpenseModal}
			onClose={() => setShowExpenseModal(false)}
			onCreate={(expense) => {
				setShowExpenseModal(false);
				setExpenses([expense, ...expenses]);
			}}
		/>
		<Modal open={showInfoModal} size='tiny' onClose={()=>{setShowInfoModal(false); setSelectedBudget(null);}}>
			<Modal.Header>Información de presupuesto</Modal.Header>
			{selectedBudget && !selectedBudget.expense_id && !selectedBudget.deleted && !!selectedBudget.approved && !selectedBudget.rejected && hasAccess(UserAccess.EVENTS.BUDGET_TO_EXPENSE) && (
				<Toolbar stretch style={{ height: 40 }}>
					<Toolbar.Item text='Convertir a gasto' iconName='money-bill' onClick={()=> onShowGenerateExpense(selectedBudget)} />
				</Toolbar>
			)}
			{selectedBudget && <Modal.Content style={{ paddingBottom: 0 }}>
				<Table
					striped
					fitted
					details
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						...(selectedBudget.date_id ? [['Evento', selectedBudget.event_name || <Icon name='minus'/>]] : []),
						['Monto', '$' + addCommas(selectedBudget.amount)],
						['Estatus', <div className={classNames("fr label", {
							blue: selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid,
							green: selectedBudget.paid && !selectedBudget.deleted,
							red: selectedBudget.deleted || selectedBudget.rejected,
							yellow: !selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid
						})}>
							{selectedBudget.deleted ? 'Eliminado' : selectedBudget.rejected ? 'Rechazado' : selectedBudget.paid ? 'Pagado' : selectedBudget.approved ? 'Aprobado' : 'Pendiente'}
						</div>],
						...(selectedBudget.rejected ? [['Razón de rechazo', selectedBudget.reject_reason_name || <Icon name='minus'/>]] : []),
						...(selectedBudget.expense_id ? [['Gasto generado', <Label color='green'>#{selectedBudget.expense_id}</Label>]] : []),
						['Descripción', selectedBudget.description || <Icon name='minus'/>],
					]}
				/>
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=>{setShowInfoModal(false); setSelectedBudget(null);}} />
			</Modal.Actions>
		</Modal>
		<Modal open={showGenerateExpenseModal} onClose={()=>{ setShowGenerateExpenseModal(false); setSelectedBudget(null); }} size='tiny'>
			<Modal.Header>¿Generar gasto?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Generar gasto desde presupuesto?'/>
				<Table 
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Titulo', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_id ? <Label style={{ width: 100 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Monto', '$' + addCommas(selectedBudget.amount)],
						['Descripción', selectedBudget.description || <Icon name='minus'/>],
						...(selectedBudget.date_id ? [['Evento', selectedBudget.event_name || <Icon name='minus'/>]] : []),
					]}
				/>
				<Checkbox label='Es una reposición' checked={!!selectedBudget.requiere_date_id} onChange={(v)=>{
					setSelectedBudget({...selectedBudget, requiere_date_id: v});
				}} style={{ marginTop: 15, marginBottom: 15 }}/>
				{!!selectedBudget.requiere_date_id && (
					<Field label='Cuenta bancaria'>
						<Input
							value={[selectedBudget.account?.name_account, selectedBudget.account?.bank].filter(Boolean).join(' - ')}
							placeholder='Selecciona una cuenta...'
							readonly
							button={
								<Button
									text='Seleccionar'
									onClick={onShowAccountsModal}
								/>
							}
						/>
					</Field>
				)}
				<Field label='Método de pago'>
					<Dropdown 
						selection 
						value={selectedMethodId} 
						onChange={(e, { value })=>setSelectedMethodId(value as number)} 
						placeholder='Método de pago' 
						options={paymentMethods.map(m=>({
							text: m.method_name,
							value: m.method_id,
							key: m.method_id
						}))} 
					/>
				</Field>
				<Field label='Archivo (Opcional)'>
					<UploadBox
						ref={uploadBoxRef}
						icon={getAssetTypeIcon(fileTypeId)}
						type={'normal'}
						text={selectedFile ? selectedFile.name : null}
						maxFileSize={2.5*1024*1024}
						onFile={onFileSelected}
					/>
				</Field>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=>{ setShowGenerateExpenseModal(false); setSelectedBudget(null); }} />
				<Button text='Generar' color='black' onClick={budgetToExpense}/>
			</Modal.Actions>
		</Modal>
		<Modal open={showAccountsModal} onClose={bindClick(setShowAccountsModal, false)} size='mini'>
			<Modal.Header>Selecciona la cuenta</Modal.Header>
			<Modal.Content>
				<Items
					selectable
					data={accounts}
					onSelected={(a)=>onAccountSelected(a)}
					render={a=>{
						return <div className="ar card placeholder account">
							<Icon name={a.clabe ? 'landmark' : 'credit-card'} />
							<div className="left">
								<div className="title">
									{a.clabe ? <>
										<b>SPEI</b> **{a.clabe.slice(-5)}
									</> : <>
										<b>{a.bank}</b> **{a.card_number.slice(-4)}
									</>}
								</div>
								<div className="name">{a.name_account}</div>
							</div>
						</div>
					}}
				/>
				<Items
					data={[0]}
					selectable
					render={()=>{
						return <Link to={'/accounts'} target='_blank' style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
							<div style={{ fontSize: 18, fontWeight: 'bold', textAlign: 'center', padding: 5 }}>Agregar Cuenta</div>
						</Link> 
					}}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShowAccountsModal, false)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default UserExpenses;